import { useState } from "react";
import { Matcher } from "react-day-picker";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import LongContentWrapper from "@/components/common/LongContentWrapper";
import { cn } from "@/lib/utils";
import { setToEndOfDay } from "@/helpers/date";

type DatePickerProps = {
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
  placeholder?: string;
  triggerClassName?: string;
  className?: string;
  disabled?: Matcher | Matcher[] | undefined;
};

export function DatePicker({
  date,
  setDate,
  placeholder = "Pick a date",
  triggerClassName,
  className,
  disabled,
}: DatePickerProps) {
  const [showCalendar, setShowCalendar] = useState(false);

  return (
    <Popover
      open={showCalendar}
      onOpenChange={(open) => {
        if (!open) setShowCalendar(false);
      }}
      modal
    >
      <PopoverTrigger
        className={cn("hover:text-inherit", triggerClassName)}
        asChild
      >
        <Button
          className={cn(
            "text-secondary-foreground justify-start gap-2 text-left font-normal min-w-50",
            !date && "text-muted-foreground",
            className,
          )}
          onClick={() => setShowCalendar(true)}
        >
          <span
            onClick={(e) => {
              e.stopPropagation();
              setDate(setToEndOfDay(new Date()));
            }}
            className="cursor-pointer transition-colors"
          >
            <CalendarIcon className="h-3 w-3" />
          </span>
          <LongContentWrapper>
            {date ? format(date, "MM/dd/yyyy") : <span>{placeholder}</span>}
          </LongContentWrapper>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="center" side="right">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(date) => {
            const endOfDay = date ? setToEndOfDay(date) : undefined;
            setDate(endOfDay);
            setShowCalendar(false);
          }}
          disabled={disabled}
        />
      </PopoverContent>
    </Popover>
  );
}
